import {Children, type EventHandler, type SyntheticEvent} from 'react'

import type {NullSuggestion, Suggestion, TextInputCompatibleChild, Trigger} from './types'

const singleWordTriggerTerminators = new Set([' ', '\n'])
const multiWordTriggerTerminators = new Set(['.', '\n'])

/**
 * Calculate whether or not suggestions should be shown based on the given state of the
 * input. If they should be shown, returns the show event.
 */
export const calculateSuggestionsQuery = (triggers: Trigger[], text: string, caretLocation: number) => {
  // Sort so we consider longest triggers first; if two triggers overlap (ie `@` and `@a`) then the longest should win
  const sortedTriggers = triggers.sort((a, b) => b.triggerChar.length - a.triggerChar.length)

  // Build backwards from the caret location until the most recent trigger or terminator
  for (
    let i = caretLocation - 1, query = '', potentialTriggers = sortedTriggers;
    i >= 0 && potentialTriggers.length > 0;
    i--
  ) {
    const character = text[i] ?? ''
    query = character + query

    if (singleWordTriggerTerminators.has(character)) potentialTriggers = potentialTriggers.filter(t => t.multiWord)
    if (multiWordTriggerTerminators.has(character)) potentialTriggers = potentialTriggers.filter(t => !t.multiWord)

    for (const trigger of potentialTriggers.filter(t => query.startsWith(t.triggerChar))) {
      const prevChar = text[i - 1] ?? ''
      const nextChar = query[1] ?? ''

      // The trigger should only activate when the trigger character is the first character in the input, is preceded
      // by whitespace(newline, tab, or space), or is preceded by an opening bracket.
      // Also, the trigger should not activate if immediately followed by whitespace (ie, the user inserted a
      // newline after the trigger character) - even for 'multi-word' triggers that allow internal whitespace.
      if ((!prevChar || /[\s([]/.test(prevChar)) && (!nextChar || !/\s/.test(nextChar)))
        return {trigger, query: query.slice(trigger.triggerChar.length)}

      potentialTriggers = potentialTriggers.filter(t => t !== trigger)
    }
  }

  return null
}

export const isNullSuggestion = (suggestion: Suggestion): suggestion is NullSuggestion =>
  typeof suggestion === 'object' && suggestion.value === null

export const getSuggestionValue = (suggestion: Suggestion): string | null =>
  typeof suggestion === 'string' ? suggestion : suggestion.value

export const getSuggestionKey = (suggestion: Suggestion): string => {
  if (typeof suggestion === 'string') return suggestion
  if (suggestion.value === null) return suggestion.key
  return suggestion.key ?? suggestion.value
}

/**
 * Replace a section of a string.
 */
export const replaceSlice = (
  original: string,
  [startInclusive, endExclusive]: [number, number],
  replacement: string,
) => {
  const before = original.substring(0, startInclusive)
  const after = original.substring(endExclusive)
  return before + replacement + after
}

/**
 * Attempts to assert that the child element is of a supported type. This can't be enforced
 * by the type system so it has to be done as a runtime check. This isn't foolproof - a
 * component that forwards a ref to a correct element but does not forward event handlers
 * will not work. But it's the best we can reasonably do.
 */
export function requireChildrenToBeInput(
  child: React.ReactElement,
  childRef: React.RefObject<HTMLElement | null>,
): TextInputCompatibleChild {
  // eslint-disable-next-line @eslint-react/no-children-only
  Children.only(child) // Assert that the child is lonely
  if (
    // There is no way to know what type the underlying child is until it mounts, so this
    // will always pass on first render before failing on the second render
    childRef.current &&
    !(childRef.current instanceof HTMLInputElement) &&
    !(childRef.current instanceof HTMLTextAreaElement)
  ) {
    throw new TypeError(
      `AutocompleteTextarea child must be a component that forwards a ref and props to an <input> or <textarea> element.`,
    )
  }
  // @ts-expect-error React 19 - Props is of type `Unknown` so we cannot validate it further
  return child
}

/**
 * Combine several event handlers into one. The last handler in the list is called first
 * and no further handlers will be called if `event.preventDefault()` is called.
 */
export const augmentHandler =
  <E extends SyntheticEvent>(...handlers: Array<EventHandler<E> | undefined>) =>
  (event: E) => {
    for (const handler of [...handlers].reverse()) {
      if (!event.isDefaultPrevented()) handler?.(event)
    }
  }
